import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  AvailableBookmakerEntry,
  LeagueBookmakerEntry,
  LeagueBookmakerLeague,
} from '@/types/leagueBookmakers';
import styles from '@/pages/admin/LeagueBookmakersTab.module.scss';

interface MappingEditorProps {
  league: LeagueBookmakerLeague;
  selectedBookmakers: LeagueBookmakerEntry[];
  availableBookmakers: AvailableBookmakerEntry[];
  draftIds: number[];
  isDirty: boolean;
  isSaving: boolean;
  onMove: (index: number, direction: -1 | 1) => void;
  onRemove: (bookmakerId: number) => void;
  onAdd: (bookmakerId: number) => void;
  onSave: () => void;
  onReset: () => void;
  onClear: () => void;
  onDiscover: () => void;
}

// Combine selectedBookmakers from the server with the available pool so that
// the editor can render the draft list in priority order even when items were
// moved between the two columns locally before saving.
function buildEntryLookup(
  selected: LeagueBookmakerEntry[],
  available: AvailableBookmakerEntry[],
): Record<number, LeagueBookmakerEntry | AvailableBookmakerEntry> {
  const map: Record<number, LeagueBookmakerEntry | AvailableBookmakerEntry> = {};
  for (const item of selected) map[item.id] = item;
  for (const item of available) map[item.id] = item;
  return map;
}

export const MappingEditor: React.FC<MappingEditorProps> = ({
  league,
  selectedBookmakers,
  availableBookmakers,
  draftIds,
  isDirty,
  isSaving,
  onMove,
  onRemove,
  onAdd,
  onSave,
  onReset,
  onClear,
  onDiscover,
}) => {
  const { t } = useTranslation('admin');

  const lookup = useMemo(
    () => buildEntryLookup(selectedBookmakers, availableBookmakers),
    [selectedBookmakers, availableBookmakers],
  );

  // Available list = available entries from server that aren't currently in the draft.
  const visibleAvailable = useMemo(
    () => availableBookmakers.filter((b) => !draftIds.includes(b.id)),
    [availableBookmakers, draftIds],
  );

  return (
    <div className={styles.rightPane}>
      <div className={styles.editorHeader}>
        <div className={styles.editorTitle}>
          {league.logoUrl && (
            <img src={league.logoUrl} alt="" className={styles.leagueLogo} />
          )}
          <span>{league.name}</span>
          {isDirty && <span className={styles.dirtyBadge}>{t('unsavedChanges')}</span>}
        </div>
        <div className={styles.editorActions}>
          <button
            type="button"
            className="ls-btn ls-btn--outlined"
            onClick={onDiscover}
          >
            🔍 {t('discoverFromApi')}
          </button>
        </div>
      </div>

      <p className={styles.hint}>{t('selectionAlgorithmHint')}</p>

      <div className={styles.editorColumns}>
        <div className={styles.editorColumn}>
          <h4 className={styles.editorColumnTitle}>{t('selectedBookmakers')}</h4>
          {draftIds.length === 0 ? (
            <div className={styles.emptyState}>{t('selectionListEmpty')}</div>
          ) : (
            draftIds.map((id, index) => {
              const entry = lookup[id];
              if (!entry) return null;
              const isDisabled = !entry.isEnabled;
              const isFirst = index === 0;
              const isLast = index === draftIds.length - 1;
              return (
                <div key={id} className={styles.priorityRow}>
                  <span className={styles.priorityIndex}>
                    {t('priorityBadge', { n: index + 1 })}
                  </span>
                  <div className={styles.reorderButtons}>
                    <button
                      type="button"
                      className={styles.reorderBtn}
                      onClick={() => onMove(index, -1)}
                      disabled={isFirst}
                      aria-label={t('moveUp')}
                    >
                      ▲
                    </button>
                    <button
                      type="button"
                      className={styles.reorderBtn}
                      onClick={() => onMove(index, 1)}
                      disabled={isLast}
                      aria-label={t('moveDown')}
                    >
                      ▼
                    </button>
                  </div>
                  <span className={styles.priorityName}>
                    {entry.name}
                    {isDisabled && (
                      <span className={`${styles.badge} ${styles.badgeWarning}`} style={{ marginLeft: 8 }}>
                        {t('bookmakerDisabledShort')}
                      </span>
                    )}
                  </span>
                  <button
                    type="button"
                    className={styles.removeBtn}
                    onClick={() => onRemove(id)}
                  >
                    × {t('removeFromPriority')}
                  </button>
                </div>
              );
            })
          )}
        </div>

        <div className={styles.editorColumn}>
          <h4 className={styles.editorColumnTitle}>{t('availableBookmakers')}</h4>
          {visibleAvailable.length === 0 ? (
            <div className={styles.emptyState}>{t('noBookmakersAvailable')}</div>
          ) : (
            visibleAvailable.map((b) => (
              <div key={b.id} className={styles.priorityRow}>
                <span className={styles.priorityName}>{b.name}</span>
                <button
                  type="button"
                  className={styles.addBtn}
                  onClick={() => onAdd(b.id)}
                >
                  + {t('addToPriority')}
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      <div className={styles.editorActions}>
        <button
          type="button"
          className="ls-btn"
          onClick={onSave}
          disabled={!isDirty || isSaving}
        >
          {t('saveMapping')}
        </button>
        <button
          type="button"
          className="ls-btn ls-btn--outlined"
          onClick={onReset}
          disabled={!isDirty || isSaving}
        >
          {t('resetMapping')}
        </button>
        {selectedBookmakers.length > 0 && (
          <button
            type="button"
            className="ls-btn ls-btn--outlined"
            onClick={onClear}
            disabled={isSaving}
          >
            {t('clearMapping')}
          </button>
        )}
      </div>
    </div>
  );
};

export default MappingEditor;
